"use client";

/**
 * One trail of an event's programme, whole, for a client going, opened from its
 * Ver trilho: its facts and photos, its track drawn on a map image with the way
 * to its start, the climbing it does, then what the programme says about it for
 * this event and its facilities. What it asks to bring is on the event's own
 * list. One column, a desktop's the same as a phone's, in a window 495 wide.
 *
 * Loaded when it opens, from the event's registered-only trail route
 * (GET /public-api/events/my-events/:id/trail/:trailId), since the programme
 * carries neither the track nor the trail's details. The route answers only a
 * client going, so the popup says so when it cannot load.
 */
import { useEffect, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Check, Clock, LocationEdit, MountainSnow, Route, RotateCw } from "lucide-react";
import { api } from "@/lib/api";
import { km, metres } from "@/lib/format";
import type { TrailDetails, TrailFacility } from "@/lib/types/event";
import { Sheet } from "@/components/ui/Sheet";
import { Loading } from "@/components/ui/Spinner";
import { DifficultyBar } from "@/components/event/DifficultyBar";
import { ElevationProfile, SlopeLegend } from "@/components/event/ElevationProfile";
import { PhotoStrip } from "@/components/event/PhotoStrip";
import { galleryMedia } from "@/components/event/media";
import { FlyoverTile } from "@/components/event/FlyoverTile";
import { durationText, routeTypeLabel } from "@/components/event/labels";
import { mapsHref, routeImageUrl } from "@/components/event/maps";

/** `variantId` says which of the trail's routes the entry opened walks: a variant's id, or 0 for the main route. */
export type TrailPopupTarget = { eventId: number | string; trailId: number | string; variantId?: number; title?: string | null };

/**
 * The popup in its sheet: `target` null keeps it closed.
 */
export function TrailPopup({ target, onClose }: { target: TrailPopupTarget | null; onClose: () => void }) {
  return (
    <Sheet open={!!target} onClose={onClose} title={target?.title || "Trilho"} width={495} maxHeight="88vh">
      {target && <TrailPopupBody eventId={target.eventId} trailId={target.trailId} variantId={target.variantId} />}
    </Sheet>
  );
}

const known = (value: number | null | undefined): value is number => value !== null && value !== undefined;

/**
 * The facts of a trail -- how long, roughly how long it takes, how much it climbs
 * and comes down, how high it goes, how hard and the shape of the route -- as a
 * row of chips. `brief` leaves it at how far, how long and how hard.
 */
export function TrailFacts({ trail, brief = false, className = "" }: { trail: Partial<TrailDetails>; brief?: boolean; className?: string }) {
  const facts: { icon: typeof Route; text: string }[] = [];
  if (Number(trail.length) > 0) {
    facts.push({ icon: Route, text: km(Number(trail.length) / 1000) });
  }
  const duration = durationText(trail.duration_hours);
  if (duration) {
    facts.push({ icon: Clock, text: `${duration} (aprox.)` });
  }
  if (!brief && Number(trail.ascent) > 0) {
    facts.push({ icon: ArrowUpRight, text: `${metres(trail.ascent)} de subida` });
  }
  if (!brief && Number(trail.descent) > 0) {
    facts.push({ icon: ArrowDownRight, text: `${metres(trail.descent)} de descida` });
  }
  if (!brief && known(trail.min_elevation) && known(trail.max_elevation)) {
    facts.push({ icon: MountainSnow, text: `${metres(trail.min_elevation)} a ${metres(trail.max_elevation)} de altitude` });
  }
  if (!brief && trail.route_type) {
    facts.push({ icon: RotateCw, text: routeTypeLabel(trail.route_type) });
  }
  if (!facts.length && !trail.difficulty) {
    return null;
  }
  return (
    <div className={`flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-[13px] text-ink-soft ${className}`}>
      {facts.map(({ icon: Icon, text }) => (
        <span key={text} className="inline-flex items-center gap-1.5">
          <Icon size={14} className="text-brand" /> {text}
        </span>
      ))}
      <DifficultyBar difficulty={trail.difficulty} />
    </div>
  );
}

/** What a trail has along it -- toilets, picnic tables and the like -- as a ticked list; nothing for a trail with none. */
export function TrailFacilities({ facilities }: { facilities: TrailFacility[] | null | undefined }) {
  const items = (facilities || []).map((item) => ({ title: String(item.title || "").trim(), description: String(item.description || "").trim() }));
  if (!items.length) {
    return null;
  }
  return (
    <div>
      <h4 className="mb-1 text-[14px] font-bold">Instalações</h4>
      <ul className="flex flex-col gap-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-[14px]">
            <Check size={16} className="mt-0.5 shrink-0 text-brand" />
            <span>
              {item.title}
              {item.description && item.description.toLowerCase() !== item.title.toLowerCase() && (
                <small className="block text-[12.5px] text-ink-soft">{item.description}</small>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TrailPopupBody({ eventId, trailId, variantId }: { eventId: number | string; trailId: number | string; variantId?: number }) {
  const [trail, setTrail] = useState<TrailDetails | null | undefined>(undefined);
  const [mapFailed, setMapFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get<TrailDetails>(`/public-api/events/my-events/${eventId}/trail/${trailId}${variantId === undefined ? "" : `?variant=${variantId}`}`, { silent: true }).then((answer) => {
      if (!cancelled) {
        setTrail(answer);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [eventId, trailId, variantId]);

  if (trail === undefined) {
    return <Loading className="py-16" />;
  }
  if (!trail) {
    return <p className="px-6 pb-10 pt-4 text-center text-[14.5px] text-ink-soft">O trilho só pode ser aberto por quem está inscrito neste evento.</p>;
  }

  const mapSrc = routeImageUrl(trail);
  const start = mapsHref(trail.google_maps_link, trail.starting_point);
  const content = String(trail.content || "").trim();

  return (
    <div className="flex flex-col gap-4 px-4 pb-6">
      <TrailFacts trail={trail} />
      <PhotoStrip photos={galleryMedia(trail.photos, trail.videos)} />
      <FlyoverTile flyover={trail.flyover} trailId={trail.id} />
      <div>
        {mapSrc && !mapFailed ? (
          <button
            type="button"
            className="pressable block w-full overflow-hidden rounded-[12px] bg-line"
            onClick={() => window.open(mapSrc, "_blank", "noopener")}
            aria-label="Abrir o mapa do percurso"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={mapSrc} alt="Mapa do percurso" className="aspect-[640/400] w-full object-cover" onError={() => setMapFailed(true)} />
          </button>
        ) : (
          <div className="flex aspect-[640/400] items-center justify-center rounded-[12px] bg-ground text-[13px] text-ink-mute">Mapa indisponível</div>
        )}
        {start && (
          <a href={start} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-brand">
            <LocationEdit size={15} /> Navegar até ao início{trail.trailhead ? ` · ${trail.trailhead}` : ""}
          </a>
        )}
      </div>
      {elevationShown(trail) && (
        <div>
          <h4 className="mb-1.5 text-[14px] font-bold">Perfil de elevação</h4>
          <div className="h-16 w-full overflow-hidden rounded-[10px] border border-line bg-surface">
            <ElevationProfile track={trail.track} className="h-full" />
          </div>
          <SlopeLegend className="mt-1.5" />
        </div>
      )}
      {content && (
        <div>
          <h4 className="mb-1 text-[14px] font-bold">Neste evento</h4>
          <p className="whitespace-pre-line text-[14px] leading-relaxed text-ink-soft">{content}</p>
        </div>
      )}
      <TrailFacilities facilities={trail.facilities} />
    </div>
  );
}

// a GPX recorded without heights draws nothing, and the heading goes with it
const elevationShown = (trail: TrailDetails) => (trail.track || []).filter((p) => typeof p[2] === "number").length >= 2;
